import { useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../components/AuthContext/context';
import { SetTermsAndConditionsAcceptedDocument } from '../../graphql/mutation/users/SetTermsAndConditionsAccepted.generated';
import { Navbar } from '../../modules/dashboard/components/Navbar/Navbar';
import { Content } from './Content';
import { Layout } from './Layout';

export const TermsAcceptance = () => {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [setTermsAndConditionsAccepted, { loading }] = useMutation(SetTermsAndConditionsAcceptedDocument, {
    refetchQueries: ['Session'],
  });

  useEffect(() => {
    if (session?.terms_and_conditions_accepted) {
      navigate('/');
    }
  }, [navigate, session]);

  const onCheckedChange = useCallback((_, checked) => {
    setTermsAccepted(checked);
  }, []);

  const onConfirm = useCallback(async () => {
    if (!termsAccepted) return;

    await setTermsAndConditionsAccepted({
      variables: {
        accepted: termsAccepted,
        date: new Date().toISOString(),
        id: session?.id,
      },
    });
  }, [setTermsAndConditionsAccepted, session?.id, termsAccepted]);

  if (session?.terms_and_conditions_accepted) {
    return <Loader />;
  }

  return (
    <Layout
      navbar={<Navbar userName={session?.fullName ?? ''} onLogoutClick={signOut} />}
      content={<Content />}
      checkbox={
        <FormControlLabel
          control={
            <Box
              sx={{
                mt: -1,
              }}
            >
              <Checkbox
                onChange={onCheckedChange}
                data-test="checkbox-terms"
                sx={{ height: 33, padding: 1, width: 33 }}
              />
            </Box>
          }
          label={
            <Typography variant="body1" component="span" data-test="confirmation-text">
              <Typography
                component="p"
                sx={{
                  lineHeight: '18px',
                }}
              >
                I have read and understand and accept the OpenXSelect Portal Terms of Service.
              </Typography>
              <Typography
                component="p"
                sx={{
                  lineHeight: '18px',
                }}
              >
                By checking this box, I agree that I am authorized to accept these Terms of Service on behalf of my
                entity.
              </Typography>
            </Typography>
          }
          sx={{
            alignItems: 'flex-start',
            marginTop: 2,
          }}
        />
      }
      actionCancel={
        <Button variant="text" onClick={signOut} disabled={loading} data-test="cancel-button">
          Cancel
        </Button>
      }
      actionConfirm={
        <Button color="primary" onClick={onConfirm} disabled={!termsAccepted || loading} data-test="continue-button">
          Continue
        </Button>
      }
    />
  );
};
